/**
 * Route guard helpers for first-party /manage child routes.
 */

import { notFound } from '@tanstack/react-router';
import { canBypassModuleRoleRequirements } from '@/utils/authz';
import type { TenantRoleType } from '@/constants/tenantRoles';

/**
 * Creates a `beforeLoad` guard that requires the user to hold at least one of
 * the given tenant roles in their JWT `tenantRoles` array.
 *
 * Admin-equivalent users (ADMIN, DEVELOPER) bypass tenant role checks — same
 * policy as {@link createModuleRouteGuard} and navigation filtering.
 */
export function createManageRouteGuard(options: {
  requiredTenantRoles: TenantRoleType[];
}) {
  return ({ context }: { context: { auth?: { role?: string; tenantRoles?: string[] } | null } }) => {
    const auth = context?.auth;
    if (!auth) return;

    if (canBypassModuleRoleRequirements(auth.role)) {
      return;
    }

    const tenantRoles = auth.tenantRoles || [];
    const hasRequiredRole = options.requiredTenantRoles.some((role) =>
      tenantRoles.includes(role)
    );

    if (!hasRequiredRole) {
      throw notFound();
    }
  };
}
